import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  query,
  orderBy,
  limit,
  onSnapshot,
  type Unsubscribe,
  type Firestore,
  FirestoreDataConverter,
  deleteField,
} from 'firebase/firestore';
import type { StrategyBlock } from '@segmently/schema-models/strategy';
import { CoreFirebaseService } from './CoreFirebaseService';
import { strategyBlockConverter as clientStrategyBlockConverter } from '../converters/strategyBlockConverter';
import { StrategyStepNodeService } from './StrategyStepNodeService';
import { validateStrategyBlockWrite } from '../types/strategyBlockWriteValidation';
import { getAgentApiBaseUrl } from './BlockLibraryMutationApi';

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }

  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function stripUndefinedDeep<T>(value: T): T {
  if (value === undefined) {
    return null as T;
  }

  if (Array.isArray(value)) {
    return value.map(item => stripUndefinedDeep(item)) as T;
  }

  if (!isPlainObject(value)) {
    return value;
  }

  return Object.entries(value).reduce<Record<string, unknown>>((result, [key, child]) => {
    if (child !== undefined) {
      result[key] = stripUndefinedDeep(child);
    }
    return result;
  }, {}) as T;
}

/**
 * StrategyBlockService
 *
 * Type-safe service for StrategyBlock subcollection CRUD operations.
 *
 * Blocks are subcollections under Strategy: projects/{projectId}/strategies/{strategyId}/blocks
 *
 * Features:
 * - Full CRUD operations for blocks within a strategy
 * - Cascade delete for screens subcollection
 * - Ordered by 'order' field (not 'createdAt')
 * - Lazy loading support with limit parameter
 * - Access to strategy block StepNode screens via getStepNodeService()
 * - Uses strategyBlockConverter for Date↔Timestamp↔ISO conversion
 *
 * Related Implementation:
 * - Sprint 3 (comprehensive tests and cascade delete refinement)
 * - Sprint 2, T018 (initial implementation for cascade delete)
 */
export class StrategyBlockService {
  private readonly projectId: string;
  private readonly strategyId: string;
  private readonly firestore = CoreFirebaseService.getInstance().firestore;
  private readonly converter: FirestoreDataConverter<StrategyBlock>;

  /**
   * Create a new StrategyBlockService instance
   *
   * @param projectId - Parent project ID
   * @param strategyId - Parent strategy ID
   * @param converter - Firestore converter for StrategyBlock entity
   */
  constructor(
    projectId: string,
    strategyId: string,
    converter: FirestoreDataConverter<StrategyBlock> = clientStrategyBlockConverter
  ) {
    this.projectId = projectId;
    this.strategyId = strategyId;
    this.converter = converter;
  }

  /**
   * Get collection path for blocks subcollection
   */
  private get collectionPath(): string {
    return `projects/${this.projectId}/strategies/${this.strategyId}/blocks`;
  }

  /**
   * Create a new block in this strategy
   *
   * @param data - Partial block data
   * @returns {Promise<StrategyBlock>} The created block
   */
  async create(data: Partial<StrategyBlock>): Promise<StrategyBlock> {
    try {
      validateStrategyBlockWrite(data);
      const collectionRef = collection(this.firestore, this.collectionPath);
      const docRef = doc(collectionRef).withConverter(this.converter);

      // Generate entityId if not provided (UUID for semantic references)
      const entityId = data.entityId || crypto.randomUUID();

      const blockData: StrategyBlock = {
        ...data,
        id: docRef.id,
        entityId,
        createdAt: new Date(),
        updatedAt: new Date(),
      } as StrategyBlock;

      await setDoc(docRef, stripUndefinedDeep(blockData));

      return blockData;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'StrategyBlockService.create');
      throw error;
    }
  }

  /**
   * Get a block by ID
   *
   * @param id - Block ID
   * @returns {Promise<StrategyBlock | null>} The block or null if not found
   */
  async get(id: string): Promise<StrategyBlock | null> {
    try {
      const docRef = doc(this.firestore, this.collectionPath, id).withConverter(this.converter);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      const data = docSnap.data();
      return { ...data, id: docSnap.id } as StrategyBlock;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'StrategyBlockService.get');
      throw error;
    }
  }

  /**
   * Update an existing block
   *
   * @param id - Block ID
   * @param data - Partial block data to update
   * @returns {Promise<StrategyBlock>} The updated block
   */
  async update(id: string, data: Partial<StrategyBlock>): Promise<StrategyBlock> {
    try {
      const clearsCustomSubtype = Object.prototype.hasOwnProperty.call(data, 'customSubtype')
        && data.customSubtype === undefined;
      const changesSubtypePair = data.blockType !== undefined || data.customSubtype !== undefined || clearsCustomSubtype;
      const existing = changesSubtypePair ? await this.get(id) : undefined;
      if (changesSubtypePair && !existing) {
        throw new Error(`StrategyBlock ${id} not found`);
      }
      const validationExisting = clearsCustomSubtype && existing
        ? { ...existing, customSubtype: undefined }
        : existing;
      validateStrategyBlockWrite(data, validationExisting ?? undefined);
      const docRef = doc(this.firestore, this.collectionPath, id).withConverter(this.converter);

      const updateData = {
        ...data,
        updatedAt: new Date(),
      };

      const sanitizedUpdate = stripUndefinedDeep(updateData as StrategyBlock) as unknown as Record<string, unknown>;
      await setDoc(docRef, {
        ...sanitizedUpdate,
        ...(clearsCustomSubtype ? { customSubtype: deleteField() } : {}),
      } as any, { merge: true });

      const updated = await this.get(id);
      if (!updated) {
        throw new Error(`StrategyBlock ${id} not found after update`);
      }

      return updated;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'StrategyBlockService.update');
      throw error;
    }
  }

  /**
   * Delete a block with cascade delete for screens subcollection
   *
   * Deletes:
   * 1. All screens in this block (blocks/{id}/screens)
   * 2. The block document itself
   *
   * @param id - Block ID
   * @returns {Promise<void>}
   */
  async delete(id: string): Promise<void> {
    try {
      await this.deleteStrategyBlockOnBackend(id);
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'StrategyBlockService.delete');
      throw error;
    }
  }

  /**
   * The backend owns the task + library lifecycle transaction. Deletion must
   * stop if that boundary is temporarily unavailable; deleting the block
   * first would leave a pending/running task with no target to finalize.
   */
  private async deleteStrategyBlockOnBackend(blockId: string): Promise<void> {
    const token = await CoreFirebaseService.getInstance().auth.currentUser?.getIdToken();
    if (!token) throw new Error('Authentication is required to delete a strategy block.');
    const response = await fetch(
      `${getAgentApiBaseUrl()}/api/agent/projects/${encodeURIComponent(this.projectId)}`
        + `/strategies/${encodeURIComponent(this.strategyId)}/blocks/${encodeURIComponent(blockId)}`
        + `?projectId=${encodeURIComponent(this.projectId)}`,
      {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    if (response.ok) return;
    const payload = await response.json().catch(() => ({})) as { error?: string };
    throw new Error(payload.error || `Strategy block deletion failed (${response.status}).`);
  }

  /**
   * List blocks in this strategy
   *
   * @param options - Query options
   * @param options.limit - Maximum number of blocks to return
   * @returns {Promise<StrategyBlock[]>} Array of blocks ordered by 'order' field
   */
  async list(options: { limit?: number } = {}): Promise<StrategyBlock[]> {
    try {
      const collectionRef = collection(this.firestore, this.collectionPath).withConverter(this.converter);

      // Sprint 5 Fix: Changed from orderBy('order') to orderBy('createdAt') to fix missing blocks issue
      let q = query(collectionRef, orderBy('createdAt', 'desc'));

      if (options.limit) {
        q = query(q, limit(options.limit));
      }

      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      } as StrategyBlock));
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'StrategyBlockService.list');
      throw error;
    }
  }

  /**
   * Subscribe to real-time collection updates
   *
   * Sets up a real-time listener for blocks collection with automatic updates.
   *
   * Pattern (from StrategiesListView - Sprint 1):
   * ```typescript
   * const unsubscribe = strategyBlockService.onCollectionSnapshot(
   *   (blocks) => {
   *     console.log('Blocks updated:', blocks);
   *     setBlocks(blocks);
   *   },
   *   (error) => {
   *     console.error('Subscription error:', error);
   *   },
   *   { limit: 50 }
   * );
   *
   * // Later: cleanup
   * unsubscribe();
   * ```
   *
   * @param onNext - Callback for successful updates
   * @param onError - Callback for errors
   * @param options - Query options (limit, orderByField, orderDirection)
   * @returns Unsubscribe function to stop listening
   */
  onCollectionSnapshot(
    onNext: (blocks: StrategyBlock[]) => void,
    onError: (error: Error) => void,
    options: {
      limit?: number;
      orderByField?: 'order' | 'createdAt' | 'updatedAt';
      orderDirection?: 'asc' | 'desc';
    } = {}
  ): Unsubscribe {
    try {
      console.log('[StrategyBlockService] onCollectionSnapshot called');
      console.log('[StrategyBlockService] this.firestore:', this.firestore);
      console.log('[StrategyBlockService] this.collectionPath:', this.collectionPath);
      console.log('[StrategyBlockService] typeof this.firestore:', typeof this.firestore);

      const collectionRef = collection(this.firestore, this.collectionPath).withConverter(this.converter);

      // Default options - Sprint 5 Fix: Changed default from 'order' to 'createdAt'
      const pageLimit = options.limit || 50;
      const orderField = options.orderByField || 'createdAt';
      const direction = options.orderDirection || 'desc';

      // Build query with ordering and limit
      const q = query(collectionRef, orderBy(orderField, direction), limit(pageLimit));

      // Subscribe to query snapshots
      return onSnapshot(
        q,
        (snapshot) => {
          // Map documents through converter
          const blocks: StrategyBlock[] = snapshot.docs.map(doc => ({
            ...doc.data(),
            id: doc.id
          } as StrategyBlock));

          onNext(blocks);
        },
        (error) => {
          CoreFirebaseService.getInstance().logError(error, 'StrategyBlockService.onCollectionSnapshot');
          onError(error as Error);
        }
      );
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'StrategyBlockService.onCollectionSnapshot.setup');
      throw error;
    }
  }

  /**
   * Clone a block with all its screens (deep copy)
   *
   * Creates a complete copy of the block with:
   * - New block ID
   * - " (Copy)" appended to name
   * - All screens cloned with new IDs
   * - No references to original entities
   *
   * Implementation Notes:
   * - Uses sequential operations for clarity and error handling
   * - System fields (id, createdAt, updatedAt) are regenerated by create()
   * - Empty blocks (no screens) are handled gracefully
   *
   * Related Implementation:
   * - Sprint 6, Task T044 (GREEN Phase)
   * - Sprint 6, Task T046 (REFACTOR Phase)
   * - Tests: T040, T042 (RED Phase)
   *
   * @param id - Original block ID to clone
   * @returns {Promise<StrategyBlock>} The cloned block with new ID
   * @throws {Error} If block not found
   */
  async cloneBlock(id: string): Promise<StrategyBlock> {
    try {
      // Step 1: Get original block
      const original = await this.get(id);
      if (!original) {
        throw new Error(`StrategyBlock ${id} not found`);
      }

      // Step 2: Create new block with copied data
      // Note: id=undefined lets create() generate new ID and timestamps
      const clonedBlock = await this.create({
        ...original,
        name: `${original.name} (Copy)`,
        id: undefined,
        entityId: crypto.randomUUID(),  // Generate new UUID for clone
      } as Partial<StrategyBlock>);

      console.log(`[StrategyBlockService] Cloned block: ${original.id} -> ${clonedBlock.id}`);

      // Step 3: Get all V2 StepNode screens from original block
      const originalScreensService = this.getStepNodeService(id);
      const originalScreens = await originalScreensService.listScreens();

      if (originalScreens.length === 0) {
        console.log(`[StrategyBlockService] No screens to clone`);
        return clonedBlock;
      }

      console.log(`[StrategyBlockService] Cloning ${originalScreens.length} screens...`);

      // Step 4: Clone each V2 StepNode screen
      const clonedScreensService = this.getStepNodeService(clonedBlock.id!);

      for (const originalScreen of originalScreens) {
        await clonedScreensService.addScreen({
          ...originalScreen,
          name: `${originalScreen.name} (Copy)`,
        });
      }

      console.log(`[StrategyBlockService] Clone complete: ${clonedBlock.id}`);

      return clonedBlock;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'StrategyBlockService.cloneBlock');
      throw error;
    }
  }

  /**
   * Get StrategyStepNodeService for this block's V2 StepNode screens.
   *
   * @param blockId - Block ID
   * @returns {StrategyStepNodeService} V2 StepNode service instance scoped to this block
   */
  getStepNodeService(blockId: string): StrategyStepNodeService {
    return new StrategyStepNodeService(this.projectId, this.strategyId, blockId);
  }

  /**
   * Deprecated compatibility alias for older tests/docs.
   * Active clone paths use V2 StepNode methods on the returned service.
   */
  getScreensService(blockId: string): any {
    return this.getStepNodeService(blockId);
  }
}
